import React, { useCallback } from 'react';
import { css } from '@emotion/css';
import { useNavigate } from 'react-router-dom';
import BannerImage from './images/banner@1x.jpg';
import { useGetUserAccountDetails } from '@/apiHooks/userAccount.hook';
import { GRAY100 } from '@/domain/colors';
import { useIntl } from '@/i18n/hooks/useIntl';
import { PlusBadge } from '@/components/common/display/plusBadge/PlusBadge';
import { VoucherUpsellBanner } from '@/components/common/VoucherUpsellBanner/VoucherUpsellBanner';
import { useVoucherEligibility } from '@/components/common/VoucherUpsellBanner/useVoucherEligibility.hook';
import { VOUCHER_UPSELL_PARAM } from '@/components/common/VoucherUpsellBanner/constants';
import { BASE_PATH, MIETER_PLUS_PATH, WOHNEN_PLUS_PATH } from '@/domain/routes';

const styles = {
  content: {
    background: 'rgb(51, 51, 51)',
    color: `${GRAY100}`,
    maxHeight: 'calc(100% - 300px)',
    minHeight: '245px',
    height: '45%',
    '@media (min-width: 576px)': {
      height: 'calc(100% - 340px)',
    },
  },
  contentLeft: {
    background: 'rgb(51, 51, 51)',
    color: `${GRAY100}`,
    maxHeight: 'calc(100% - 300px)',
    minHeight: '245px',
    height: '45%',
    '@media (min-width: 576px)': {
      height: 'calc(100% - 340px)',
    },
    paddingTop: 'var(--spacing-600)',
    paddingBottom: 'var(--spacing-600)',
    paddingLeft: 'var(--spacing-1000) !important',
    paddingRight: 'var(--spacing-1000) !important',
  },
  contentLeftVoucher: {
    color: `${GRAY100}`,
    paddingTop: 'var(--spacing-600)',
    paddingBottom: 'var(--spacing-600)',
    paddingLeft: 'var(--spacing-1000) !important',
    paddingRight: 'var(--spacing-1000) !important',
  },
  title: {
    textAlign: 'center',
    paddingTop: 'var(--spacing-1000)',
    paddingBottom: 'var(--spacing-600)',
    marginBottom: '0',
    color: 'inherit',
  },
  titleLeft: {
    textAlign: 'left',
    marginBottom: '0',
    color: 'inherit',
  },
  description: {
    textAlign: 'center',
    paddingBottom: 'var(--spacing-1000)',
    color: 'var(--color-text-white)',
  },
  descriptionLeft: {
    textAlign: 'left',
    marginBottom: 'var(--spacing-1000)',
    color: 'var(--color-text-white)',
  },
  image: {
    background: `url(${BannerImage}) no-repeat`,
    minHeight: '300px',
    maxHeight: 'calc(100% - 245px)',
    height: '55%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    '@media (min-width: 576px)': {
      height: '340px',
    },
  },
  imageVoucher: {
    background: `url(${BannerImage}) no-repeat`,
    height: '300px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  button: {
    marginBottom: '25px',
    '@media (min-width: 576px)': {
      marginBottom: '30px',
    },
  },
  modal: {
    maxWidth: '760px',
    position: 'relative',
    backgroundColor: 'rgb(51, 51, 51)',
    flex: 1,
    overflowY: 'auto',
    zIndex: 0,
    '@media (max-width: 767px)': {
      height: '100%',
    },
    '@media screen and (max-height: 710px)': {
      height: '100%',
    },
  },
  modalVoucher: {
    maxWidth: '760px',
    position: 'relative',
    flex: 1,
    overflowY: 'auto',
    zIndex: 0,
    '@media (max-width: 767px)': {
      height: '100%',
    },
    '@media screen and (max-height: 710px)': {
      height: '100%',
    },
  },
  divider: {
    borderColor: '#484848 !important',
  },
} as const;

interface BannerProps {
  variant: 'mieten' | 'wohnen';
  onClose: () => void;
}

export const Banner = ({ onClose, variant }: BannerProps) => {
  const { userAccountDetails } = useGetUserAccountDetails();
  const { t } = useIntl();
  const navigate = useNavigate();
  const { isEligible: isVoucherEligible } = useVoucherEligibility();

  const handleOpenVoucherModal = useCallback(() => {
    const basePath = variant === 'mieten' ? `${BASE_PATH}${MIETER_PLUS_PATH}` : `${BASE_PATH}${WOHNEN_PLUS_PATH}`;
    navigate(`${basePath}?${VOUCHER_UPSELL_PARAM}=true`);
  }, [navigate, variant]);

  const welcomeText = userAccountDetails?.firstName
    ? t(
        { id: 'welcomeModal.banner.welcomeText', defaultMessage: 'Herzlichen Glückwunsch' },
        { FirstName: userAccountDetails.firstName },
      )
    : t({ id: 'welcomeModal.banner.welcomeTextNoName', defaultMessage: 'Herzlichen Glückwunsch' });

  const descriptionText = t(
    { id: 'welcomeModal.banner.title', defaultMessage: 'Ab sofort kannst du alle Vorteile nutzen!' },
    { BuyerPlusLogo: <PlusBadge variant={variant === 'mieten' ? 'rent' : 'living'} whiteLogo={true} /> },
  );

  // Women's Day voucher content
  if (isVoucherEligible) {
    return (
      <div className={`${css(styles.modalVoucher)}`}>
        <div className={`${css(styles.imageVoucher)}`} />
        <div
          className={`display-flex flex-column grid-justify-center grid-align-start ${css(styles.contentLeftVoucher)}`}
        >
          <h3 className={`font-heading-large-bold ${css(styles.titleLeft)}`} data-test-ctx='welcome-profile-test'>
            {welcomeText}
          </h3>
          <p
            className={`font-heading-small-regular ${css(styles.descriptionLeft)}`}
            data-test-ctx={variant === 'mieten' ? 'mieterplus-test' : 'wohnenplus-test'}
          >
            {descriptionText}
          </p>
          <VoucherUpsellBanner variant='vibrant' onCloseParentModal={handleOpenVoucherModal} source='successModal' />
        </div>
      </div>
    );
  }

  // Default content
  return (
    <div data-testid='bannerModal' className={`${css(styles.modal)}`}>
      <div className={`${css(styles.image)}`} />
      <div className={`grid-flex flex-column grid-justify-center grid-align-center ${css(styles.content)}`}>
        <h3 className={`${css(styles.title)}`} data-test-ctx='welcome-profile-test'>
          {welcomeText}
        </h3>
        <p
          className={`${css(styles.description)}`}
          data-test-ctx={variant === 'mieten' ? 'mieterplus-test' : 'wohnenplus-test'}
        >
          {descriptionText}
        </p>
        <button className={`${css(styles.button)} button-rounded button-primary`} onClick={onClose}>
          {t({ id: 'common.buttons.next', defaultMessage: 'Weiter' })}
        </button>
      </div>
    </div>
  );
};
